import React, { useState, useEffect } from 'react';
import {
  Row, Col, Card, Table, Tag, Typography, Space, Button, Modal, Form,
  Input, Select, DatePicker, TimePicker, Upload, message, Popconfirm, Image
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined,
  CalendarOutlined, TeamOutlined, WifiOutlined
} from '@ant-design/icons';
import { getEvents, createEvent, updateEvent, deleteEvent, uploadEventLogo } from '../api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const EVENT_TYPES = ['Conference', 'Exhibition', 'Seminar', 'Workshop', 'Summit', 'Expo', 'Live Event', 'Other'];
const STATUS_COLOR = { upcoming: 'blue', active: 'green', completed: 'default', cancelled: 'red' };

export default function EventDashboard() {
  const [events, setEvents]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [logoModal, setLogoModal] = useState(null);
  const [logoFile, setLogoFile]   = useState(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    getEvents().then(r => setEvents(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); form.resetFields(); form.setFieldsValue({ status: 'upcoming', event_type: 'Conference', country: 'India' }); setModalOpen(true); };
  const openEdit   = (rec) => {
    setEditing(rec);
    form.setFieldsValue({
      ...rec,
      start_date: rec.start_date ? dayjs(rec.start_date) : null,
      end_date:   rec.end_date   ? dayjs(rec.end_date)   : null,
      start_time: rec.start_time ? dayjs(rec.start_time, 'HH:mm:ss') : null,
      end_time:   rec.end_time   ? dayjs(rec.end_time,   'HH:mm:ss') : null,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const vals = await form.validateFields();
      const payload = {
        ...vals,
        start_date: vals.start_date?.format('YYYY-MM-DD'),
        end_date:   vals.end_date?.format('YYYY-MM-DD'),
        start_time: vals.start_time?.format('HH:mm:ss'),
        end_time:   vals.end_time?.format('HH:mm:ss'),
      };
      if (editing) { await updateEvent(editing.id, payload); message.success('Event updated'); }
      else         { await createEvent(payload);             message.success('Event created'); }
      setModalOpen(false); load();
    } catch (e) { if (!e.errorFields) message.error(e.response?.data?.message || 'Save failed'); }
  };

  const handleLogoUpload = async () => {
    if (!logoFile || !logoModal) return;
    try {
      const fd = new FormData();
      fd.append('logo', logoFile);
      await uploadEventLogo(logoModal.id, fd);
      message.success('Logo uploaded');
      setLogoModal(null); setLogoFile(null); load();
    } catch (e) { message.error('Upload failed'); }
  };

  const columns = [
    { title: '#', width: 50, render: (_, __, i) => i + 1 },
    {
      title: 'Event', render: (_, r) => (
        <Space>
          {r.logo_url
            ? <Image src={`${window.location.origin}${r.logo_url}`} width={36} height={36} style={{ borderRadius: 6, objectFit: 'cover' }} preview={false} />
            : <div style={{ width: 36, height: 36, borderRadius: 6, background: '#0F1D6B18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#0F1D6B' }}><CalendarOutlined /></div>
          }
          <div>
            <Text strong>{r.event_name}</Text><br />
            <Text type="secondary" style={{ fontSize: 11 }}>{r.event_code} · {r.event_type}</Text>
          </div>
        </Space>
      ),
    },
    { title: 'Organizer', dataIndex: 'organizer', render: v => v || '-' },
    { title: 'Venue / City', render: (_, r) => <Text style={{ fontSize: 12 }}>{r.venue}{r.city ? `, ${r.city}` : ''}</Text> },
    {
      title: 'Dates', render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <div>{r.start_date ? dayjs(r.start_date).format('DD MMM YYYY') : '-'} → {r.end_date ? dayjs(r.end_date).format('DD MMM YYYY') : '-'}</div>
          <Text type="secondary">{r.start_time?.slice(0, 5)} – {r.end_time?.slice(0, 5)}</Text>
        </div>
      ),
    },
    { title: 'Delegates', dataIndex: 'delegate_count', render: v => <Tag icon={<TeamOutlined />}>{v || 0}</Tag>, width: 95 },
    { title: 'Readers',   dataIndex: 'reader_count',   render: v => <Tag icon={<WifiOutlined />}>{v || 0}</Tag>,  width: 85 },
    { title: 'Status', dataIndex: 'status', render: v => <Tag color={STATUS_COLOR[v]}>{v?.toUpperCase()}</Tag>, width: 100 },
    {
      title: 'Actions', width: 160, render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<UploadOutlined />} onClick={() => setLogoModal(r)}>Logo</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete this event?" onConfirm={() => deleteEvent(r.id).then(() => { message.success('Deleted'); load(); })}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: '#0F1D6B' }}>
            <CalendarOutlined style={{ marginRight: 8 }} />Event Dashboard
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Register and manage events, exhibitions and live shows</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} size="large" style={{ background: '#0F1D6B' }}>
            Register Event
          </Button>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {[
          { label: 'Total Events', count: events.length, color: '#0F1D6B' },
          { label: 'Active',       count: events.filter(e => e.status === 'active').length,    color: '#52c41a' },
          { label: 'Upcoming',     count: events.filter(e => e.status === 'upcoming').length,  color: '#1890ff' },
          { label: 'Completed',    count: events.filter(e => e.status === 'completed').length, color: '#999' },
        ].map((s, i) => (
          <Col xs={12} md={6} key={i}>
            <Card size="small" style={{ borderTop: `4px solid ${s.color}`, borderRadius: 10 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.count}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>{s.label}</Text>
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ borderRadius: 10 }}>
        <Table columns={columns} dataSource={events} rowKey="id" loading={loading}
          size="small" scroll={{ x: 1100 }} pagination={{ pageSize: 10, showSizeChanger: true }} />
      </Card>

      <Modal title={editing ? `Edit Event: ${editing.event_name}` : 'Register New Event'}
        open={modalOpen} onOk={handleSave} onCancel={() => setModalOpen(false)}
        width={760} okText={editing ? 'Update' : 'Create Event'}
        okButtonProps={{ style: { background: '#0F1D6B' } }} destroyOnClose>
        <Form form={form} layout="vertical" size="middle">
          <Row gutter={16}>
            <Col span={16}><Form.Item name="event_name" label="Event Name" rules={[{ required: true }]}><Input placeholder="e.g. Tech Summit 2025" /></Form.Item></Col>
            <Col span={8}><Form.Item name="event_code" label="Event Code" rules={[{ required: true }]}><Input placeholder="TECHSUMMIT25" /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="event_type" label="Event Type" rules={[{ required: true }]}><Select>{EVENT_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}</Select></Form.Item></Col>
            <Col span={8}><Form.Item name="status" label="Status"><Select>{['upcoming','active','completed','cancelled'].map(s => <Option key={s} value={s}>{s}</Option>)}</Select></Form.Item></Col>
            <Col span={8}><Form.Item name="organizer" label="Organizer"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={16}><Form.Item name="venue" label="Venue"><Input placeholder="Venue name" /></Form.Item></Col>
            <Col span={8}><Form.Item name="city" label="City"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}><Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="end_date" label="End Date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="start_time" label="Start Time"><TimePicker format="HH:mm" style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="end_time" label="End Time"><TimePicker format="HH:mm" style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="website" label="Website"><Input placeholder="https://" /></Form.Item></Col>
            <Col span={12}><Form.Item name="country" label="Country"><Input placeholder="India" /></Form.Item></Col>
          </Row>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} placeholder="Event description..." /></Form.Item>
        </Form>
      </Modal>

      <Modal title={`Upload Logo – ${logoModal?.event_name}`} open={!!logoModal}
        onOk={handleLogoUpload} onCancel={() => { setLogoModal(null); setLogoFile(null); }}
        okText="Upload Logo">
        <Upload beforeUpload={f => { setLogoFile(f); return false; }} maxCount={1}
          accept="image/*" listType="picture-card">
          <div><UploadOutlined /><div style={{ marginTop: 8 }}>Select Logo</div></div>
        </Upload>
        <Text type="secondary" style={{ fontSize: 11 }}>Accepted: JPG, PNG, SVG · Max 5MB</Text>
      </Modal>
    </div>
  );
}
